'use client';

import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import styles from '../styles/catalog.module.css';
import { UserContext } from '../context/UserContext';
import Navbar from './Navbar';
import Footer from './footer/Footer';

import CatalogFilters from '@/components/catalogFilters.jsx';
import { filterCourses } from '@/components/catalogFilters.js';
import { generateSlug } from '@/lib/slugify';

const FILTERS = [
  'All',
  'Coding & Technology',
  'English',
  'Art & Creativity',
  'Social Studies',
];

/* ---------------------------------
   Helpers
----------------------------------*/
function getHeroImageUrl(imageUrl) {
  if (!imageUrl) return imageUrl;
  if (imageUrl.includes('/card/') && imageUrl.includes('_250x200.webp')) {
    return imageUrl
      .replace('/card/', '/hero/')
      .replace('_250x200.webp', '_500xauto.webp');
  }
  return imageUrl;
}

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleWithPRNG(list, prng) {
  const arr = [...(list || [])];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(prng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function normalizeId(id) {
  return String(id);
}

/* ---------------------------------
   Catalog Page
----------------------------------*/
export default function CatalogPage({ initialCourses, initialUser }) {
  const router = useRouter();
  const user = useContext(UserContext) || initialUser;

  const [courses, setCourses] = useState(initialCourses || []);
  const [featuredCourseIds, setFeaturedCourseIds] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);

  const [coursesLoaded, setCoursesLoaded] = useState(
    Array.isArray(initialCourses) && initialCourses.length > 0
  );
  const [featuredCoursesLoaded, setFeaturedCoursesLoaded] = useState(false);

  // Filters state
  const [ageFilterEnabled, setAgeFilterEnabled] = useState(false);
  const [selectedAges, setSelectedAges] = useState([]);

  const [priceFilterEnabled, setPriceFilterEnabled] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const [dayFilterEnabled, setDayFilterEnabled] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);

  const [dateRange, setDateRange] = useState('All upcoming dates');

  // stable seed per page load for non-featured random order
  const seedRef = useRef(
    (() => {
      if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
        const buf = new Uint32Array(1);
        window.crypto.getRandomValues(buf);
        return buf[0];
      }
      return Math.floor(Math.random() * 0xffffffff);
    })()
  );

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const savedFilter = sessionStorage.getItem('catalogFilter');

      if (savedFilter) {
        const match = FILTERS.find(
          (label) => label.toLowerCase() === savedFilter.toLowerCase()
        );
        if (match) setActiveFilter(match);
        sessionStorage.removeItem('catalogFilter');
      }
    } catch (e) {
      console.warn('Could not read catalogFilter from sessionStorage', e);
    }
  }, []);

  useEffect(() => {
    if (!initialCourses || initialCourses.length === 0) {
      loadCourses();
    } else {
      setCourses(initialCourses);
      setCoursesLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCourses]);

  useEffect(() => {
    loadFeaturedCourses();
  }, []);

  async function loadCourses() {
    try {
      setCoursesLoaded(false);

      const res = await fetch('/api/catalog', { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setCourses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading courses:', err);
      setCourses([]);
    } finally {
      setCoursesLoaded(true);
    }
  }

  async function loadFeaturedCourses() {
    try {
      setFeaturedCoursesLoaded(false);

      const res = await fetch('/api/classes/featured', { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      const ids = Array.isArray(data?.courses)
        ? data.courses.map((course) => normalizeId(course.id)).filter(Boolean)
        : [];

      setFeaturedCourseIds(ids);
    } catch (err) {
      console.error('Error loading featured courses:', err);
      setFeaturedCourseIds([]);
    } finally {
      setFeaturedCoursesLoaded(true);
    }
  }

  async function addNewCourse(courseData) {
    try {
      const res = await fetch('/api/catalog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(courseData),
      });
      if (!res.ok) throw new Error('Failed to add course');

      await Promise.all([loadCourses(), loadFeaturedCourses()]);
    } catch (err) {
      console.error('Error adding new course:', err);
      alert('Error adding new course');
    }
  }

  async function deleteCourse(courseId) {
    if (!confirm('Are you sure you want to delete this course?')) return;

    try {
      const res = await fetch(`/api/catalog/${courseId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete course');

      await Promise.all([loadCourses(), loadFeaturedCourses()]);
    } catch (err) {
      console.error('Error deleting course:', err);
    }
  }

  function handleCourseClick(course) {
    router.push(`/classes/${generateSlug(course.name, course.id)}`);
  }

  const filteredCourses = useMemo(() => {
    return filterCourses(courses, activeFilter, {
      ageFilterEnabled,
      selectedAges,
      priceFilterEnabled,
      minPrice,
      maxPrice,
      dayFilterEnabled,
      selectedDays,
      dateRange,
    });
  }, [
    courses,
    activeFilter,
    ageFilterEnabled,
    selectedAges,
    priceFilterEnabled,
    minPrice,
    maxPrice,
    dayFilterEnabled,
    selectedDays,
    dateRange,
  ]);

  const orderedCourses = useMemo(() => {
    if (!filteredCourses.length) return [];

    const featuredIdSet = new Set(featuredCourseIds);

    const featured = [];
    const nonFeatured = [];

    for (const course of filteredCourses) {
      const normalizedCourseId = normalizeId(course.id);

      if (featuredIdSet.has(normalizedCourseId)) {
        featured.push(course);
      } else {
        nonFeatured.push(course);
      }
    }

    // keep exact API order for featured courses
    const featuredMap = new Map(
      featured.map((course) => [normalizeId(course.id), course])
    );

    const orderedFeatured = featuredCourseIds
      .map((id) => featuredMap.get(id))
      .filter(Boolean);

    // randomize only normal courses
    const prng = mulberry32(seedRef.current);
    const shuffledNonFeatured = shuffleWithPRNG(nonFeatured, prng);

    return [...orderedFeatured, ...shuffledNonFeatured];
  }, [filteredCourses, featuredCourseIds]);

  const isCatalogReady = coursesLoaded && featuredCoursesLoaded;

  return (
    <div className={styles.pageWrapper}>
      <header className={styles.header}>
        <Navbar allCourses={courses} showSearch={false} />
      </header>

      <main className={styles.mainContent}>
        <section className={styles.catalogHeader}>
          <div
            className={styles.filterBar}
            role="tablist"
            aria-label="Filter classes by topic"
          >
            {FILTERS.map((label) => (
              <button
                key={label}
                role="tab"
                aria-selected={activeFilter === label}
                className={
                  activeFilter === label
                    ? `${styles.filterBtn} ${styles.filterBtnActive}`
                    : styles.filterBtn
                }
                onClick={() => setActiveFilter(label)}
              >
                {label}
              </button>
            ))}
          </div>

          <CatalogFilters
            ageFilterEnabled={ageFilterEnabled}
            setAgeFilterEnabled={setAgeFilterEnabled}
            selectedAges={selectedAges}
            setSelectedAges={setSelectedAges}
            priceFilterEnabled={priceFilterEnabled}
            setPriceFilterEnabled={setPriceFilterEnabled}
            minPrice={minPrice}
            setMinPrice={setMinPrice}
            maxPrice={maxPrice}
            setMaxPrice={setMaxPrice}
            dayFilterEnabled={dayFilterEnabled}
            setDayFilterEnabled={setDayFilterEnabled}
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
            dateRange={dateRange}
            setDateRange={setDateRange}
            styles={styles}
          />
        </section>

        <section className={styles.courseListSection}>
          {!isCatalogReady ? (
            <p className={styles.emptyState}>Loading classes...</p>
          ) : (
            <>
              <section className={styles.courseList}>
                {orderedCourses.map((course) => (
                  <div
                    key={course.id}
                    className={styles.courseCard}
                    onClick={() => handleCourseClick(course)}
                  >
                    <img
                      src={
                        getHeroImageUrl(course?.imageUrl) ||
                        '/images/default-flag-classes.png'
                      }
                      alt={course?.name || 'Course image'}
                      className={styles.courseImage}
                    />

                    <div className={styles.courseContent}>
                      <div className={styles.courseHeader}>
                        <h3 className={styles.courseTitle}>{course?.name}</h3>
                        <span className={styles.price}>
                          $
                          {(
                            (course?.sidebar?.weeklyPrice || 0) /
                            (course?.sidebar?.weeklyFrequency || 1)
                          ).toFixed(2)}{' '}
                          per class
                        </span>
                      </div>

                      <p className={styles.meta}>
                        <span>
                          <img src="/images/group-icon.svg" alt="Group Icon" />
                          Group Class
                        </span>
                        <span>
                          <img src="/images/age-icon.svg" alt="Age Icon" />
                          Ages: {course?.ages}
                        </span>
                        <span>
                          <img src="/images/clock-icon.svg" alt="Clock Icon" />
                          {course?.sidebar?.courseDuration || 0} wks,{' '}
                          {course?.sidebar?.weeklyFrequency || 1}/wk,{' '}
                          {course?.sidebar?.singleClassDuration || '1 hr'}
                        </span>
                      </p>

                      <p className={styles.description}>{course?.description}</p>

                      {course?.category && (
                        <span className={styles.categoryBadge}>
                          {course.category}
                        </span>
                      )}

                      {user &&
                        (user.role === 'ADMIN' ||
                          user.role === 'SUPER_ADMIN') && (
                          <button
                            className={styles.deleteCourseBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCourse(course.id);
                            }}
                          >
                            Delete Course
                          </button>
                        )}
                    </div>
                  </div>
                ))}
              </section>

              {!orderedCourses.length && (
                <p className={styles.emptyState}>No classes available yet.</p>
              )}
            </>
          )}
        </section>

        {showAddCourseModal && (
          <AddCourseModal
            onClose={() => setShowAddCourseModal(false)}
            onAddCourse={addNewCourse}
          />
        )}
      </main>

      <Footer />
    </div>
  );
}

/* ---------------------------------
   AddCourseModal (Admin Only)
----------------------------------*/
function AddCourseModal({ onClose, onAddCourse }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ages, setAges] = useState('');
  const [price, setPrice] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    const courseData = { name, description, ages, price };
    await onAddCourse(courseData);
    onClose();
    setName('');
    setDescription('');
    setAges('');
    setPrice('');
  }

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <span className={styles.close} onClick={onClose}>
          &times;
        </span>

        <h2>Add New Course</h2>

        <form onSubmit={handleSubmit} className={styles.addCourseForm}>
          <label htmlFor="name">Course Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            name="description"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <label htmlFor="ages">Ages:</label>
          <input
            type="text"
            id="ages"
            name="ages"
            required
            value={ages}
            onChange={(e) => setAges(e.target.value)}
          />

          <label htmlFor="price">Price per class:</label>
          <input
            type="number"
            id="price"
            name="price"
            step="0.5"
            required
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />

          <button type="submit">Add Course</button>
        </form>
      </div>
    </div>
  );
}